/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { MouseEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { sanitizeHtmlAndFillInEmbedPage } from '../../../utils';
import { fetchDocumentVersionThunk } from '../../chatSlice';
import { DocumentVersionType, PageTypeEnum } from '../../../algorithm/types';
import { snackNotifications } from '../../../notification/SnackBarUtils';
import styles from './RenderedHTML.module.scss';
import { useUpdateFhirSidebar } from '../../../Fhir/useHooksFhirSidebar';
import useCopyableActionContent from '../useHooks';
import { closeFullScreenImage, makeImagesExpandable, updateFhirTags } from './renderedHtmlUtils';
import { analyticsTrack } from '../../../analytics_utils';
import { selectPearlUserInfo } from '../../../Login/userSlice';
import clsx from 'clsx';

interface RenderedHTMLProps {
  content: string;
  className?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: () => void;
  documentVersionId: string | undefined;
  pageId: string | undefined;
  placeholder?: string;
  creatorMode?: boolean;
  // For analytics, the documentId, slug and name of the page being rendered
  documentId?: string | undefined;
  documentSlug?: string | undefined;
  pageName?: string | undefined;
  inlineDiv?: boolean; // If true, the outer div will be inline instead of block, which is useful for rendering small pieces of html such as question tooltips.  Note that this will not make all inner divs inline, so it won't work for large pieces of html that have divs inside them that need to be inline.
  expandActionIconsOnRender?: boolean; // If true, all <action> blocks are expanded after render/update.
}

export function RenderedHTML({
  content,
  className,
  onClick,
  onMouseLeave,
  pageId,
  placeholder,
  creatorMode,
  documentVersionId,
  // For analytics, the documentId, slug and name of the page being rendered
  documentId,
  documentSlug,
  pageName,
  inlineDiv,
  expandActionIconsOnRender = false,
}: RenderedHTMLProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const disableSanitizer = useSearchParams()[0].has('noSanitizer');
  if (disableSanitizer) console.log('Sanitizer disabled');
  const html = useMemo(
    () => sanitizeHtmlAndFillInEmbedPage(documentVersionId, pageId, content, disableSanitizer),
    [content, disableSanitizer, documentVersionId, pageId]
  );

  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const publishedDocuments = useAppSelector((state) => state.chat.publishedDocuments);
  const prepublishedDocuments = useAppSelector((state) => state.chat.prepublishedDocuments);
  const navigate = useNavigate();
  // Keep track of what embedded docs we have found so far, to prevent inifite loops and to rerun when an embedded doc is loaded because it itself
  // may have embedded docs.
  const [embeddedDocsFound, setEmbeddedDocsFound] = useState(new Set<string>());
  useEffect(() => {
    // Reset the list of found embedded docs when the content changes.
    // This is just used to prevent infinite loops when an embedded doc has an embedded doc.
    setEmbeddedDocsFound(new Set());
    // console.log('Resetting embedded docs found');
  }, [dispatch, html, publishedDocuments, disableSanitizer]);
  useCopyableActionContent(ref); // Expand <action> when copying content to clipboard

  /* Get the list of 'red box'es in the html - i.e. the words in the page content html
   * that are surrounded by a <fhir> tag.
   * When we have embeddables, when the embeddable loads we need to rerun this */
  const { doUpdateFhirSidebar } = useUpdateFhirSidebar(ref, content, undefined, pageId, documentVersionId, false);

  useEffect(() => {
    // On both load and unload, remove any fullscreen image
    closeFullScreenImage();
    return () => {
      closeFullScreenImage();
    };
  }, []);

  const updateEmbeddedDocs = useCallback(
    (html: HTMLDivElement | Element | null) => {
      html?.querySelectorAll('embeddocumentpage').forEach((node) => {
        // const docName = node.getAttribute('data-doc'); // Useful just for debug
        const docId = node.getAttribute('data-doc-id'); // note, the embedded document id, not the document version id
        const pageName = node.getAttribute('data-page'); // The name of the page inside the embedded document
        const slug = node.getAttribute('data-slug');
        if (embeddedDocsFound.has(docId || slug!)) {
          return;
        }
        const publishedOnly = !creatorMode || !node.getAttribute('data-non-published');
        if (!docId) {
          console.error('embeddocumentpage tag used but no id was supplied');
          return;
        }
        if (!pageName) {
          console.error('embeddocumentpage tag used but no page name was supplied');
          return;
        }
        let embeddedDoc: DocumentVersionType | null | undefined = publishedOnly // null means we tried to fetch but it's not there, undefined means we haven't tried yet
          ? publishedDocuments[docId]
          : prepublishedDocuments[docId];
        if (!embeddedDoc && slug) {
          embeddedDoc =
            Object.values(publishedOnly ? publishedDocuments : prepublishedDocuments).find(
              (d) => d?.document.slug === slug
            ) || embeddedDoc;
        }
        if (embeddedDoc === undefined) {
          if (slug) {
            void dispatch(fetchDocumentVersionThunk({ slug, documentId: docId, publishedOnly })).then((result) => {
              // console.log('Fetched embedded document by slug', result);
            });
          } else {
            void dispatch(fetchDocumentVersionThunk({ documentId: docId, publishedOnly })).then((result) => {
              // console.log('Fetched embedded document by documentId', result);
            });
          }
          node.innerHTML = `<i>Loading...</i>`;
        } else if (embeddedDoc === null) {
          node.innerHTML = `<div style="color: red"><i>⚠ Sorry, this embedded document was not found</i></div>`;
        } else {
          const page_id = embeddedDoc.nameToPageId[pageName]!;
          const page = embeddedDoc.pages[page_id];
          if (!page) {
            snackNotifications.warning(
              `Page "${pageName}" in document "${embeddedDoc.title}" was included but does not exist.`
            );
            console.warn(`Page "${pageName}" in document "${embeddedDoc.title}" was included but does not exist.`);
            return;
          }
          if (page.pageType !== PageTypeEnum.GUIDE) {
            snackNotifications.warning(
              `Page "${pageName}" in document "${embeddedDoc.title}" was included but is not a guide, but a ${page.pageType} page`
            );
            console.warn(
              `Page "${pageName}" in document "${embeddedDoc.title}" was included but is not a guide, but a ${page.pageType} page`
            );
            return;
          }
          if (!page.content) {
            console.warn(`Page "${pageName}" in document "${embeddedDoc.title}" was included but has no content`);
            snackNotifications.warning(
              `Page "${pageName}" in document "${embeddedDoc.title}" was included but has no content`
            );
            return;
          }
          let html = sanitizeHtmlAndFillInEmbedPage(embeddedDoc.id, page.id, page.content, disableSanitizer);
          const editHref = `/embeddable/${embeddedDoc.document.id}/${
            embeddedDoc.id
          }/edit/guide?tab=${encodeURIComponent(page.name)}`;
          const draftToggle = creatorMode
            ? `<div class="${styles.embeddableMenu}"><a href="${editHref}" target="_blank" rel="noopener noreferrer" class="${styles.publishedBadge}">Published Embeddable - Click to edit</a></div>`
            : '';
          html = draftToggle + html;
          node.innerHTML = html;
          node.querySelector(`.${styles.embeddableMenu} a`)?.addEventListener('click', (e) => {
            e.preventDefault();
            window.open(editHref, '_blank', 'noopener,noreferrer');
          });
          updateRenderedHtml(node, embeddedDoc.id, embeddedDoc.document.id, page_id, pageName, true);
          setEmbeddedDocsFound((prev) => new Set(prev.add(docId || slug!)));
          doUpdateFhirSidebar();
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, navigate, html, publishedDocuments, prepublishedDocuments, disableSanitizer, embeddedDocsFound]
  );

  const updateRenderedHtml = useCallback(
    (
      node: HTMLDivElement | Element | null,
      documentVersionId: string | undefined,
      documentId: string | undefined, // Undefined when isEmbedded is false, or string if isEmbedded is true
      pageId: string | undefined,
      pageName: string | undefined,
      isEmbedded: boolean
    ) => {
      updateEmbeddedDocs(node);
      makeImagesExpandable(node);
      if (expandActionIconsOnRender) {
        expandAllActionsRendered(node);
      }
      if (!documentVersionId || !pageId) {
        return;
      }
      updateFhirTags(node, documentVersionId, documentId, pageId, pageName, isEmbedded);
    },
    [expandActionIconsOnRender, updateEmbeddedDocs]
  );

  // When the ref changes, look inside the html div element and:
  useLayoutEffect(() => {
    const node = ref?.current;
    updateRenderedHtml(
      node,
      documentVersionId,
      undefined /* documentId is not needed when isEmbedded is false */,
      pageId || '',
      undefined /* pageName is not needed when isEmbedded is false */,
      false
    );
  }, [ref, documentVersionId, pageId, updateRenderedHtml]);

  return (
    <div className={clsx(styles.container, 'ck-content', inlineDiv ? styles.inlineContainer : '')}>
      <div
        ref={ref}
        className={className}
        id="htmlcontents-rendered"
        dangerouslySetInnerHTML={{ __html: html || (placeholder ? `<i style="color:gray">${placeholder}</i>` : '') }}
        onClick={(e) => {
          const node = (e.target as HTMLElement).closest?.('a'); // Check if the clicked element or parent is an <a>
          if (node && node instanceof HTMLAnchorElement) {
            if (node.classList.contains('internal-link')) {
              // For internal links (i.e. links to other parts of pearl.creator), we want to use navigate
              // to avoid a full page reload.
              e.preventDefault();
              const href = node.getAttribute('href');
              if (href && href.startsWith('/chat')) {
                e.preventDefault();
                navigate(href);
                return;
              }
            } else if (node.classList.contains('external-link')) {
              // For external links, we want to first send an analytics event, then open the link in a new tab.
              e.preventDefault();
              const href = node.getAttribute('href');
              if (href) {
                const domainOnly = href.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0]; // Extract domain only, e.g. example.com
                analyticsTrack(
                  'external_link_click',
                  { url: href, domainOnly, documentId, documentVersionId, pageId, documentSlug, pageName },
                  pearlUserInfo
                );
                window.open(href, '_blank', 'noopener,noreferrer');
                return;
              }
            }
          }

          // stopPropagation because we put this inside tooltips inside buttons.  Users can click links, but we don't
          // want that event to propogate to the button.
          // But have our own onClick event, because popover uses it to close the tooltip.
          e.stopPropagation();
          onClick?.(e);
        }}
        onMouseLeave={onMouseLeave}
      />
    </div>
  );
}

export function toggleAllActionsRendered(rootElement: HTMLElement | null) {
  if (!rootElement) {
    console.warn('toggleAllActions: rootElement is undefined');
    return;
  }
  const anyExpanded = expandAllActionsRendered(rootElement);
  if (!anyExpanded) {
    // All were open, so close them all.
    const actionToggles = rootElement.querySelectorAll<HTMLElement>('action');
    actionToggles.forEach((toggle) => {
      if (toggle && toggle.classList.contains('open')) {
        toggle.click();
      }
    });
  }
}

export function expandAllActionsRendered(rootElement: HTMLElement | Element | null): boolean {
  if (!rootElement) {
    return false;
  }

  let anyExpanded = false;
  const actionToggles = rootElement.querySelectorAll<HTMLElement>('action');
  actionToggles.forEach((toggle) => {
    if (toggle && !toggle.classList.contains('open')) {
      toggle.click();
      anyExpanded = true;
    }
  });

  return anyExpanded;
}
